import { ref } from 'vue';
import { httpService } from '../services/httpService';
import { eventBus } from '../eventBus';
import { log, error as logError } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/WhatsappMessageTemplates_RemoverWhatsappMessageTemplateInput.ts';
import '../contracts/WhatsappMessageTemplates_SalvarInput.ts';

import '../contracts/WhatsappMessageTemplates_BuscaWhatsappMessageTemplateResponse';
import '../contracts/WhatsappMessageTemplates_CarregaWhatsappMessageTemplatesResponse';
import '../contracts/WhatsappMessageTemplates_RemoverWhatsappMessageTemplateResponse';
import '../contracts/WhatsappMessageTemplates_SalvarResponse';
import '../contracts/WhatsappMessageTemplates_UploadMediaResponse';
import type { WhatsappMessageTemplates_SalvarInput } from '../contracts/WhatsappMessageTemplates_SalvarInput';
/**
 * Composable for WhatsApp Message Templates operations
 * Provides methods to interact with the WhatsApp Message Templates API
 */
export function useWhatsappMessageTemplates() {
  const isLoading = ref(false);
  const error = ref(null);

  /**
   * Fetch all templates for table listing
   */
  const fetchTemplates = async () => {
    log('useWhatsappMessageTemplates', 'fetchTemplates', 'Fetching all templates');
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get('/index.php/config/whatsapp_message_templates/carrega_whatsapp_message_templates', { responseContract: 'WhatsappMessageTemplates_CarregaWhatsappMessageTemplatesResponse' });
      log('useWhatsappMessageTemplates', 'fetchTemplates', 'Response', response);

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || response.error || 'Erro ao buscar templates');
      }
    } catch (err) {
      logError('useWhatsappMessageTemplates', 'fetchTemplates', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Fetch single template by ID
   */
  const fetchTemplate = async (id: any) => {
    log('useWhatsappMessageTemplates', 'fetchTemplate', 'Fetching template', { id });
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.get(`/index.php/config/whatsapp_message_templates/buscaWhatsappMessageTemplate/${id}`, { responseContract: 'WhatsappMessageTemplates_BuscaWhatsappMessageTemplateResponse' });
      log('useWhatsappMessageTemplates', 'fetchTemplate', 'Response', response);

      if (response.success) {
        return response.dados;
      } else {
        const errorMsg = response.error || 'Erro ao buscar template';
        logError('useWhatsappMessageTemplates', 'fetchTemplate', 'Backend returned error', {
          response,
          error: errorMsg
        });
        throw new Error(errorMsg);
      }
    } catch (err) {
      logError('useWhatsappMessageTemplates', 'fetchTemplate', 'Error', {
        id,
        message: err.message,
        status: err.status,
        data: err.data
      });
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Save template (create or update)
   * @param {Object} data - Template data to save
   */
  const saveTemplate = async (data: WhatsappMessageTemplates_SalvarInput) => {
    log('useWhatsappMessageTemplates', 'saveTemplate', 'Saving template', data);
    isLoading.value = true;
    error.value = null;

    try {
      validate('WhatsappMessageTemplates_SalvarInput', data);
      const response = await httpService.post('/index.php/config/whatsapp_message_templates/salvar', data, { responseContract: 'WhatsappMessageTemplates_SalvarResponse' });
      log('useWhatsappMessageTemplates', 'saveTemplate', 'Response', response);

      if (response.success) {
        // Emit appropriate event based on whether this is a create or update
        if (data.template_id) {
          eventBus.emit('whatsappMessageTemplates.updated', data.template_id);
        } else {
          eventBus.emit('whatsappMessageTemplates.created', response.dados);
        }
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar template');
      }
    } catch (err) {
      logError('useWhatsappMessageTemplates', 'saveTemplate', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Delete template by ID
   * @param {Number|String} id - Template ID to delete
   */
  const deleteTemplate = async (id: number | string) => {
    log('useWhatsappMessageTemplates', 'deleteTemplate', 'Deleting template', id);
    isLoading.value = true;
    error.value = null;

    try {
      const response = await httpService.post(`/index.php/config/whatsapp_message_templates/remover_whatsapp_message_template/${id}`, null, { inputContract: 'WhatsappMessageTemplates_RemoverWhatsappMessageTemplateInput', responseContract: 'WhatsappMessageTemplates_RemoverWhatsappMessageTemplateResponse' });
      log('useWhatsappMessageTemplates', 'deleteTemplate', 'Response', response);

      if (response.success) {
        eventBus.emit('whatsappMessageTemplates.deleted', id);
        return response;
      } else {
        throw new Error(response.error || 'Erro ao deletar template');
      }
    } catch (err) {
      logError('useWhatsappMessageTemplates', 'deleteTemplate', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Upload media file for a template
   * @param {File} file - The media file to upload
   * @param {String} templateType - The template type (image, video, audio)
   * @returns {Object} Response with media URL
   */
  const uploadMedia = async (file: any, templateType: any) => {
    log('useWhatsappMessageTemplates', 'uploadMedia', 'Uploading media', {
      fileName: file.name,
      fileSize: file.size,
      templateType
    });
    isLoading.value = true;
    error.value = null;

    try {
      const formData = new FormData();
      formData.append('media_file', file);
      formData.append('template_type', templateType);

      const response = await httpService.post('/index.php/config/whatsapp_message_templates/upload_media', formData, { responseContract: 'WhatsappMessageTemplates_UploadMediaResponse' });
      log('useWhatsappMessageTemplates', 'uploadMedia', 'Response', response);

      if (response.success) {
        // Legacy responses returned media_url at the top level; the contract puts it under dados.
        // Read from both shapes to maintain backward compatibility until all callers migrate.
        const responseWithLegacyShape = response as unknown as { media_url?: string; dados?: { media_url?: string } };
        return { media_url: responseWithLegacyShape.media_url || responseWithLegacyShape.dados?.media_url };
      } else {
        throw new Error(response.error || 'Erro ao fazer upload da midia');
      }
    } catch (err) {
      logError('useWhatsappMessageTemplates', 'uploadMedia', 'Error', err);
      error.value = err.message;
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    // State
    isLoading,
    error,

    // CRUD operations
    fetchTemplates,
    fetchTemplate,
    saveTemplate,
    deleteTemplate,

    // Media operations
    uploadMedia
  };
}
